<template>
  <div class="schedule-call-page">
    <!-- Full-width dark header bar -->
    <header class="top-header" data-test-id="schedule-call-header">
      <div class="top-header-content">
        <div class="top-header-logo" data-test-id="schedule-call-logo">
          <SvgIcon v-if="logoIconName" :name="logoIconName" class="top-logo" :width="logoWidth" />
          <img v-else-if="customLogo" :src="customLogo" alt="Logo" class="top-logo" />
        </div>
        <h1 class="top-header-title" data-test-id="schedule-call-title">Call Back Request</h1>
      </div>
    </header>

    <!-- Light blue background with overlapping form -->
    <div class="schedule-call-container" data-test-id="schedule-call-container">
      <div class="form-wrapper" data-test-id="schedule-call-form-wrapper">
        <!-- HubSpot form -->
        <div
          v-if="isFormIdValid"
          class="hs-form-frame"
          data-region="ap1"
          data-test-id="hubspot-schedule-call-form"
          :data-form-id="formId"
          :data-portal-id="hubspotPortalId"
        ></div>
        <div v-else class="form-error" data-test-id="schedule-call-form-error">
          <p class="error-message">
            The call back form is currently unavailable. Please contact support for assistance.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/common/SvgIcon.vue';

const HUBSPOT_PORTAL_ID = '441883190';
const HUBSPOT_SCRIPT_URL = `https://js-ap1.hsforms.net/forms/embed/${HUBSPOT_PORTAL_ID}.js`;

export default {
  name: 'ScheduleCall',
  components: {
    SvgIcon,
  },
  computed: {
    formId() {
      return process.env.VUE_APP_CUSTOM_THEME_HUBSPOT_SCHEDULE_CALL_FORM_ID ?? '';
    },
    customLogo() {
      return process.env.VUE_APP_CUSTOM_THEME_LOGO;
    },
    originType() {
      return (process.env.VUE_APP_ORIGIN_TYPE ?? 'bridgit').toLowerCase();
    },
    logoIconName() {
      const logoMap = {
        chl: 'chl_logo',
        aussie: 'aussie_logo',
        finsure: 'finsure_logo',
      };
      return logoMap[this.originType] ?? null;
    },
    logoWidth() {
      // Finsure logo needs to be smaller due to its aspect ratio
      if (this.logoIconName === 'finsure_logo') return 230;
      return 250;
    },
    isFormIdValid() {
      return Boolean(this.formId && this.formId.trim());
    },
  },
  mounted() {
    if (this.isFormIdValid) {
      this.loadHubSpotScript();
    }
  },
  data() {
    return {
      hubspotPortalId: HUBSPOT_PORTAL_ID,
    };
  },
  methods: {
    loadHubSpotScript() {
      if (typeof document === 'undefined') return;
      if (document.querySelector(`script[src="${HUBSPOT_SCRIPT_URL}"]`)) return;

      const script = document.createElement('script');
      script.src = HUBSPOT_SCRIPT_URL;
      script.defer = true;
      script.async = false;
      document.head.appendChild(script);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.schedule-call-page {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Full-width dark header bar */
.top-header {
  width: 100%;
  background-color: $secondary-color;
  padding: 2rem 1rem 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-header-content {
  width: 100%;
  max-width: 768px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.top-header-logo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.top-logo {
  fill: white;
  display: block;
}

.top-logo :deep(svg) {
  height: auto;
  display: block;
  max-height: none;
}

.top-header-title {
  font-family: Gordita-medium, 'HK Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

/* Light blue background container */
.schedule-call-container {
  width: 100%;
  flex: 1;
  background-color: #f1f6fb;
  padding: 0 1rem 2rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* Form wrapper that overlaps into header */
.form-wrapper {
  max-width: 800px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: visible;
  display: flex;
  flex-direction: column;
  margin-top: -1rem;
  position: relative;
  z-index: 1;
}

:deep(.hs-form-frame) {
  min-height: 400px;
  iframe {
    border: none;
    width: 100%;
  }
}

.form-error {
  padding: 2rem;
  text-align: center;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-message {
  color: #d32f2f;
  font-size: 16px;
  margin: 0;
}

@media (max-width: 768px) {
  .top-header {
    padding: 1rem;
  }

  .top-header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
    padding-bottom: 2rem;
  }

  .top-header-title {
    font-size: 20px;
    align-self: flex-end;
  }

  .form-wrapper {
    margin-top: -1.5rem;
  }

  :deep(.hs-form-frame) {
    padding: 1.5rem;
  }
}
</style>
